import React from 'react';
import { Flex, JoyKitThemeProvider, TextV2, TooltipV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { Info } from '@withjoy/joykit/icons';
import { useEventInfo } from '@shared/utils/eventInfo';
import { isEventMoreThanNDaysAway } from '@shared/utils/dateHelpers';
import { pxToRem } from '@withjoy/joykit/theme';

/**
 * Hotels typically open bookings 6-12 months in advance.
 * This threshold determines when to show the "availability coming soon" message.
 */
const HOTEL_AVAILABILITY_THRESHOLD_DAYS = 300;

/**
 * A tag that displays a message about a hotel being sold out.
 * @returns A tag that displays a message about a hotel being sold out.
 */
export const SoldOutTag = () => {
  const { tq } = useTranslation('sharedComponents');
  const { finalizedEventDate } = useEventInfo();

  const isMoreThan300Days = isEventMoreThanNDaysAway(finalizedEventDate ?? null, HOTEL_AVAILABILITY_THRESHOLD_DAYS);

  const message = isMoreThan300Days ? tq`Availability for these dates will be released closer to the event date.` : tq`This hotel is fully booked for the event dates.`;

  return (
    <JoyKitThemeProvider>
      <TooltipV2
        isOpen={isMoreThan300Days ? undefined : false}
        overrides={{
          Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white', marginLeft: 2 } },
        }}
        content={
          <Flex
            flexDirection="column"
            maxWidth={pxToRem(300)}
          >
            <TextV2
              typographyVariant="label2"
              color="mono4"
            >
              {tq`Most hotels only open bookings 6–12 months in advance. Because your event is farther out, rooms haven’t been released yet — please check back closer to your event date.`}
            </TextV2>
          </Flex>
        }
        placement="top"
        onMouseEnterDelay={500}
      >
        <Flex
          width={'100%'}
          gap={3}
          alignItems="center"
          color="mono12"
        >
          {isMoreThan300Days && (
            <Info
              size={16}
              title={tq`Hotel Availability`}
              aria-label={tq`Hotel Availability`}
            />
          )}
          <TextV2
            typographyVariant="body1"
            color="mono12"
          >
            {message}
          </TextV2>
        </Flex>
      </TooltipV2>
    </JoyKitThemeProvider>
  );
};
